import { ChatCompletionRequestMessage, Configuration, OpenAIApi } from "openai";
import { ResponseState } from "../app/components/input.model";
import { ReactFlowInstance, MarkerType, Node } from "reactflow";
import { devFlags, projectTitle, uuid } from "../app/utils";
import { timer } from 'd3-timer';
import { OpenAI } from "openai-streams";
import { getStoredApiKey } from "../app/components/ApiKeyModal";

const getOpenAIClient = () => {
  const configuration = new Configuration({
    apiKey: getStoredApiKey(),
    baseOptions: {
      headers: { 'User-Agent': undefined },
    },
  });
  return new OpenAIApi(configuration);
};

const extractTopicPrompt: ChatCompletionRequestMessage = {
  // I'm trying to push ChatGPT to respond in one term 
  role: 'user',
  content:
    `You are not a conversational agent, so don't converse
    with me. Your response is directly being transformed into a
    topic, so your text will not be able to be parsed, so please
    strip all unnecessary content from your response. This
    rule about length is very very strict. This text should be
    glancable. No quotes, no punctuation.`
};

// Object that specifies max token length by response type
export const tokens = {
  full: 1028,
  summary: 64,
  keywords: 16,
  term: 5,
};

/**
 * Update: Tried to fix the network issue, not fixed.
 */
export const getChatGPTResponseStream = async (messages: ChatCompletionRequestMessage[], prompt: string, onText: any) => {
  let retryCount = 0;

  const MAX_RETRY_COUNT = 3;
  const RETRY_DELAY = 2000;

  const makeApiCall = async () => {
    return await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ getStoredApiKey() }`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          ...messages,
          { role: 'user', content: prompt },
        ],
        model: 'gpt-3.5-turbo',
        max_tokens: 2048,
        stream: true,
      }),
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      if (!res.body) throw new Error('No response body');
      return res.body;
    });
  }

  const retry = async () => {
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    await readStream();
  }

  const readStream = async () => {
    try {
      const body = await makeApiCall();
      const decoder = new TextDecoder('utf-8');
      const reader = body.getReader();

      let fullText = '';
      let lastFire = 0;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          onText(fullText);
          return fullText;
        }

        const lines = decoder.decode(value).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ') || line.includes('[DONE]')) continue;
          try {
            const json = JSON.parse(line.slice(6));
            const content = json.choices?.[0]?.delta?.content;
            if (!content) continue;

            fullText += content;
            onText(fullText);

            //Detects punctuation, if yes, fires onText once per .5 sec
            if (/[\p{P}\p{S}]/u.test(content)) {
              const now = Date.now();
              if (now - lastFire > 500) {
                lastFire = now;
                onText(fullText);
              }
            }
          } catch (_) {
            // skip malformed lines
          }
        }
      }
    } catch (error) {
      if (retryCount < MAX_RETRY_COUNT) {
        retryCount++;
        await retry();
      } else {
        return `Error: ${error}`;
      }
    }
  }

  return await readStream();
}

// Semantic Zoom: Summarize text if zoomed out large amount
export const getChatGPTLines = async (text: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("- Response\n- lines\n- placeholder");
  }
  // If text is as short as what we're asking, don't apply lines
  // For lines, if it's just a full paragraph, don't make the api call
  if (text.length <= tokens.summary || !text.includes('\n')) {
    return Promise.resolve('');
  }

  const lines: string[] = text.split('\n').filter(text => !!(text.trim()));

  return await Promise.all(lines.map(line => getOpenAIClient().createChatCompletion({
    model: "gpt-4",
    messages: [
      {
        role: 'user',
        content: `"${line}"\n\nIf the text stated above is a paragraph, 
        summarize it to a sentence. If the text is a bullet point or numbered 
        list item, keep both the bullet point/number and main topic/term that 
        represented the entire line, but just summarize the description into keywords.`,
      }
    ],
    max_tokens: tokens.summary,
    temperature: 0.2,
  })))
  .then((lines) => {
    return lines.map(lineData => lineData.data.choices[0].message?.content.trim()
      .replaceAll('"', ""))
      .join('\n\n');
  });
};

// Semantic Zoom: Summarize text if zoomed out medium amount
export const getChatGPTSummary = async (text: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("Response summary placeholder");
  }
  // If text is as short as what we're asking, just return the text
  if (text.length <= tokens.summary) {
    return Promise.resolve(text);
  }

  const gptPrompt: string = `Summarize this text in 1-2 phrases:\n\n${text}\n\n`;

  return await getOpenAIClient().createChatCompletion({
    model: "gpt-4",
    messages: [
      {
        role: 'user',
        content: gptPrompt
      }
    ],
    max_tokens: tokens.summary,
    temperature: 0.4,
  })
  .then((data) => {
    return data.data.choices[0].message?.content.trim().replaceAll('"', "") ?? '';
  });
};

// Semantic Zoom: Summarize text if zoomed out large amount
export const getChatGPTKeywords = async (text: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("Response keywords placeholder");
  }
  // If text is as short as what we're asking, just return the text
  if (text.length <= tokens.keywords) {
    return Promise.resolve(text);
  }

  const gptPrompt: string = `Extract 3-5 of the most important key words from this text in CSV format\n\n${text}\n\n`;

  return await getOpenAIClient().createChatCompletion({
    model: "gpt-4",
    messages: [
      {
        role: 'user',
        content: gptPrompt
      }
    ],
    max_tokens: tokens.full,
    temperature: 0.2,
  })
  .then((data) => {
    return data.data.choices[0].message?.content.trim().replaceAll('"', "") ?? '';;
  });
};

export const getChatGPTOverarchingTopic = async (context: string[], setLoadingTopic: any) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("parent");
  }

  setLoadingTopic(true);
  return await getOpenAIClient().createChatCompletion({
    model: "gpt-4",
    messages: [
      extractTopicPrompt,
      {
        role: 'user',
        content: `Give me the overarching key topic in the form of a
          term in 1 to 3 words given this context: ${context}\n\n
          If you can't extract a key topic, just say ${projectTitle}`,
      }
    ],
    max_tokens: tokens.keywords,
    temperature: 0.1,
  })
  .then((data) => {
    setLoadingTopic(false);
    return data.data.choices[0].message?.content.replaceAll(/"|\./g, '').trim();
  });
}

// Overloading with Context of type ChatRequestMessage
export const getChatGPTRelatedTopics = async (context: string, nTopics: number, nMargin?: number) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve(['recommendation1', 'recommendation2', 'recommendation3']);
  }

  return await getOpenAIClient().createChatCompletion({
    model: "gpt-4",
    messages: [
      extractTopicPrompt,
      {
        role: 'user',
        content: `Give me ${nTopics} ${!nMargin ? `and only ${nTopics}` : `give or take ${nMargin}`} new subtopics in the form of
          terms in 1 to 3 words each given this context: "${context}"\n\n
          Format your response in CSV (comma separated values).`,
      }
    ],
    max_tokens: tokens.keywords,
    n: 1,
    temperature: 0.5,
  })
  .then((data) => {
    return data.data.choices[0].message?.content
      .split(',').map(topic => topic.replaceAll(/"/g, '').trim()) ?? [];
  });
}

// Generates stream of recommended questions
export const getChatGPTBrainstormStream = async (messages: ChatCompletionRequestMessage[], keyword: string, onText: any) => {
  let retryCount = 0;

  const MAX_RETRY_COUNT = 3;
  const RETRY_DELAY = 2000;

  const makeApiCall = async () => {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ getStoredApiKey() }`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          ...messages,
          {
            role: 'user',
            content: `I need to learn about ${keyword}. Give me a total of 25 questions, 
            with questions starting with "why", 5 questions starting with "what", 
            5 questions starting with "when", 5 questions starting with "where", 
            and 5 questions starting with "how". Do not add number in front of the questions.`,
          }
        ],
        model: "gpt-4",
        max_tokens: 2048,
        stream: true
      })
    });

    if (!response || !response.body) {
      throw new Error('Unable to connect to server');
    }

    return response.body;
  }

  const retry = async () => {
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    await readStream();
  }

  const readStream = async () => {
    try {
      const body = await makeApiCall();
      const decoder = new TextDecoder('utf-8');
      const reader = body.getReader();

      let fullText = '';
      let lastFire = 0;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          onText(fullText);
          return fullText;
        }

        const lines = decoder.decode(value).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ') || line.includes('[DONE]')) continue;
          try {
            const json = JSON.parse(line.slice(6));
            const content = json.choices?.[0]?.delta?.content;
            if (!content) continue;

            fullText += content;
            onText(fullText);

            //Detects punctuation, if yes, fires onText once per .5 sec
            if (/[\p{P}\p{S}]/u.test(content)) {
              const now = Date.now();
              if (now - lastFire > 500) {
                lastFire = now;
                onText(fullText);
              }
            }
          } catch (_) {
            // skip malformed lines
          }
        }
      }
    } catch (error) {
      if (retryCount < MAX_RETRY_COUNT) {
        retryCount++;
        await retry();
      } else {
        return `Error: ${error}`;
      }
    }
  }

  return await readStream();
}

// FUNCTIONS BELOW ARE ALL UNUSED

// Unused
export const getGPT3Questions = async (concept: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve([
      "placeholder questions",
      {
        why: ["placeholder question", "placeholder question", "placeholder question", "placeholder question", "placeholder question"],
        what: ["placeholder question", "placeholder question", "placeholder question", "placeholder question", "placeholder question"],
        when: ["placeholder question", "placeholder question", "placeholder question", "placeholder question", "placeholder question"],
        where: ["placeholder question", "placeholder question", "placeholder question", "placeholder question", "placeholder question"],
        how: ["placeholder question", "placeholder question", "placeholder question", "placeholder question", "placeholder question"],
      },
    ]);
  }
  const prompt: string =
    "I need to learn about " +
    concept +
    `. Give me a total of 25 questions, 
  with questions starting with "why", 5 questions starting with "when", 
  5 questions starting with "where", 5 questions starting with "how", 
  and 5 questions starting with "what".`;

  return await getOpenAIClient()
    .createChatCompletion({
      model: "gpt-4",
      messages: [{
        role: 'user',
        content: prompt,
      }],
      max_tokens: 700,
      temperature: 0.7,
      top_p: 1,
      n: 1,
      stream: false,
      stop: "",
    })
    .then((data) => {
      const text: any = data.data.choices[0].message?.content;

      let question_set: any;
      if (text !== null) {
        question_set = {
          why: text
            .match(/Why .+?$/gm)
            .map((elem: string) => elem.replace("\n", ""))
            .map((elem: string) => elem.replace("? ", "?")),
          when: text
            .match(/When .+?$/gm)
            .map((elem: string) => elem.replace("\n", ""))
            .map((elem: string) => elem.replace("? ", "?")),
          where: text
            .match(/Where .+?$/gm)
            .map((elem: string) => elem.replace("\n", ""))
            .map((elem: string) => elem.replace("? ", "?")),
          how: text
            .match(/How .+?$/gm)
            .map((elem: string) => elem.replace("\n", ""))
            .map((elem: string) => elem.replace("? ", "?")),
          what: text
            .match(/What .+?$/gm)
            .map((elem: string) => elem.replace("\n", ""))
            .map((elem: string) => elem.replace("? ", "?")),
        };
      }

      return [text, question_set];
      // if (typeof text === 'string') {

      //     let re = /\d.*\n*/g; // regex pattern

      //     let subtopics : any;
      //     subtopics = text.match(re);// put all subtopics into array

      //     if (Array.isArray(subtopics)) {
      //         // remove unnecessary characters
      //         subtopics.forEach((elem, idx) => subtopics[idx] = elem.replace(/\d. /, ''));
      //         subtopics.forEach((elem, idx) => subtopics[idx] = elem.replace(/ ?\n/, ''));
      //         return subtopics;
      //     }
      // }
    });
};

export const extendConcept = async (
  reactFlowInstance: ReactFlowInstance,
  concept: string,
  conceptnode: boolean = true,
) => {


  let topics: string[] | any;
  if (devFlags.disableOpenAI) {
    topics = [
      //   "Human Resources Management",
      //   "Financial Management",
      //   "Project Management",
      //   "Strategic Planning",
      //   "Risk Management",
      //   "Quality Management",
      "San Diego Beaches",
      "San Diego Restaurants",
      "San Diego Museums",
    ];
  } else {
    topics = await getTopics(concept);
  }

  return topics;

  // return [childNode, childEdge];
  return;
  // return childNode;

  let childNodeArray = [];
  let childEdgeArray = [];

  const options = { duration: 300 };

  // to interpolate and animate the new positions, we create objects that contain the current and target position of each node
  const transitions = childNodeArray.map((node) => {
    return {
      id: node.id,
      // this is where the node currently is placed
      from: reactFlowInstance.getNode(node.id)?.position || node.position,
      // this is where we want the node to be placed
      to: node.position,
      node,
    };
  });

    // create a timer to animate the nodes to their new positions
    const t = timer((elapsed: number) => {
      const s = elapsed / options.duration;

      const currNodes = transitions.map(({ node, from, to }) => {
        return {
          id: node.id,
          position: {
            // simple linear interpolation
            x: from.x + (to.x - from.x) * s,
            y: from.y + (to.y - from.y) * s,
          },
          data: { ...node.data },
          type: node.type,
        };
      });

      reactFlowInstance.setNodes(currNodes);

      // this is the final step of the animation
      if (elapsed > options.duration) {
        // we are moving the nodes to their destination
        // this needs to happen to avoid glitches
        const finalNodes = transitions.map(({ node, to }) => {
          return {
            id: node.id,
            position: {
              x: to.x,
              y: to.y,
            },
            data: { ...node.data },
            type: node.type,
          };
        });

        reactFlowInstance.setNodes(finalNodes);

        // stop the animation
        t.stop();

      }
    });


};

// export const getChatGPTResponseStream = async (messages: ChatCompletionRequestMessage[], onText: any) => {
//   try {
//     const response = await fetch('https://api.openai.com/v1/chat/completions', {
//       method: 'POST',
//       headers: {
//         'Authorization': `Bearer ${ getStoredApiKey() }`,
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({
//         messages,
//         model: "gpt-3.5-turbo",
//         max_tokens: 2048,
//         stream: true
//       })
//     });

//     if (!response || !response.body) return;

//     const decoder = new TextDecoder('utf8');
//     const reader = response.body.getReader();

//     let fullText = '';
//     let lastFire = 0;

//     // @ts-ignore
//     async function read() {
//       const { value, done } = await reader.read();

//       if (done) return onText(fullText);

//       // const delta = decoder.decode(value).match(/"delta":\s*({.*?"content":\s*".*?"})/)?.[1];
//       const delta = decoder.decode(value).match(/"delta":\s*({.*?"content":\s*".*?"})/)?.[1];
//       console.log(delta);

//       if (delta) {
//         const content = JSON.parse(delta).content;

//         fullText += content;

//         onText(fullText);

//         //Detects punctuation, if yes, fires onText once per .5 sec
//         if (/[\p{P}\p{S}]/u.test(content)) {
//           const now = Date.now();

//           if (now - lastFire > 500) {
//             lastFire = now
//             onText(fullText)
//           }
//         }
//       }

//       await read();
//     }

//     await read();

//     return fullText;
//   } catch (error) {
//     return `Error: ${error}`;
//   }
// }

// Unused
export const getChatGPTResponse = async (history: ChatCompletionRequestMessage[], prompt: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("Response text placeholder goes here. ChatGPT response is disabled at the moment.");
  }

  return await getOpenAIClient().createChatCompletion({
    model: "gpt-3.5-turbo",
    messages: [
      ...history,
      {
        role: 'user',
        content: prompt
      }
    ],
    max_tokens: tokens.full,
    temperature: 0.7,
  })
  .then((data) => {
    return data.data.choices[0].message?.content.trim();
  });
};

// Unused
export const getGPTTopicsNCategory = async (prompt: string, concept: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("free text");
  }
  const gptPrompt: string = `${prompt}\n\n`;

  return await getOpenAIClient()
    .createCompletion({
      model: "text-davinci-003",
      prompt: gptPrompt,
      max_tokens: 128,
      temperature: 0.7,
      top_p: 1,
      n: 1,
      stream: false,
      logprobs: null,
      stop: "",
    })
    .then((data) => {
      const text = data.data.choices[0].text;
      let subtopics: any;
      let category: string;

      // get categories
      if (typeof text === "string") {
        let re = /\d.*\n*/g; // regex pattern

        subtopics = text.match(re); // put all subtopics into array

        if (Array.isArray(subtopics)) {
          // remove unnecessary characters
          subtopics.forEach(
            (elem, idx) => (subtopics[idx] = elem.replace(/\d. /, ""))
          );
          subtopics.forEach(
            (elem, idx) => (subtopics[idx] = elem.replace(/ ?\n/, ""))
          );
          return subtopics;
        }


      }
    });
};

// Unused
export const getTopics = async (concept: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("free text");
  }

  let prompt = "Give me 3 lower level topics of " + concept;

  const gptPrompt: string = `${prompt}\n\n`;

  return await getOpenAIClient()
    .createCompletion({
      model: "text-davinci-003",
      prompt: gptPrompt,
      max_tokens: 128,
      temperature: 0.7,
      top_p: 1,
      n: 1,
      stream: false,
      logprobs: null,
      stop: "",
    })
    .then((data) => {
      const text = data.data.choices[0].text;

      if (typeof text === "string") {
        let re = /\d.*\n*/g; // regex pattern

        let subtopics: any;
        subtopics = text.match(re); // put all subtopics into array

        if (Array.isArray(subtopics)) {
          // remove unnecessary characters
          subtopics.forEach(
            (elem, idx) => (subtopics[idx] = elem.replace(/\d. /, ""))
          );
          subtopics.forEach(
            (elem, idx) => (subtopics[idx] = elem.replace(/ ?\n/, ""))
          );
          return subtopics;
        }
      }
    });
};

// Unused
export const getGPT3Term = async (history: string, prompt: string) => {
  if (devFlags.disableOpenAI) {
    return Promise.resolve("Term placeholder");
  }
  const gptPrompt: string = `${history}\n\n${prompt}\n\n`;

  return await getOpenAIClient()
    .createCompletion({
      model: "text-davinci-003",
      prompt: gptPrompt,
      max_tokens: tokens.term,
      temperature: 0.2,
      top_p: 1,
      n: 1,
      stream: false,
      logprobs: null,
      stop: "",
    })
    .then((data) => {
      return data.data.choices[0].text?.trim();
    });
};
